import React, { useRef } from "react";
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Divider,
} from "@mui/material";
import { formatDate } from "../../../../../utils/fileUtils";
import FileActionsMenu from "./FilesActionMenu";
import { getFileIconImage } from "../../../../../utils/fileUtils";
import useIsOverflow from "../../../../../components/useIsOverflow";
import { notSupportedBrowserFiles } from "./GridView";
import { makeStyles } from "tss-react/mui";
import FileStatus from "./FileStatus";
import { NavLink } from "react-router-dom";
import { VIEW_FILE_URL } from "../../../../../utils/routes";

const useStyles = makeStyles()((theme) => ({
  listViewContainer: {
    width: "100%",
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  countChip: {
    borderRadius: 4,
    fontWeight: 500,
  },
  tableContainer: {
    width: "100%",
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  tableRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    cursor: "pointer",
    "&:last-child": {
      "& td": {
        borderBottom: "none",
      },
    },
  },
  tableCell: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  fileIcon: {
    width: 30,
    height: 30,
    marginRight: theme.spacing(1),
  },
}));

function ListView({
  filteredCurrentDirectory,
  handleSelectDirectory,
  handleSelectFile,
  handleDeleteFile,
  handleUpdateFile,
}) {
  const theme = useTheme();
  const { classes } = useStyles();
  const { directories, files } = filteredCurrentDirectory;
  const hasFiles = files && files.length > 0;

  return (
    <TableContainer className={classes.tableContainer}>
      <Table>
        <TableHead sx={{ backgroundColor: theme.palette.background.light }}>
          <TableRow>
            <TableCell className={classes.tableCell} style={{ minWidth: 400, maxWidth: 700 }}>Name</TableCell>
            {hasFiles && <TableCell className={classes.tableCell}>Status</TableCell>}
            <TableCell className={classes.tableCell}>Date Modified</TableCell>
            {hasFiles && <TableCell className={classes.tableCell}>Category</TableCell>}
            {hasFiles && <TableCell className={classes.tableCell}>Tags</TableCell>}
            {hasFiles && <TableCell className={classes.tableCell} align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {directories.map((directory, index) => (
            <Directory
              key={`dir-${index}`}
              directory={directory}
              handleSelectDirectory={handleSelectDirectory}
              hasFiles={hasFiles}
            />
          ))}
          {directories.length > 0 && files.length > 0 && <Divider />}
          {files.map((file, index) => (
            <File
              key={`file-${index}`}
              file={file}
              handleSelectFile={handleSelectFile}
              handleDeleteFile={handleDeleteFile}
              handleUpdateFile={handleUpdateFile}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Directory({ directory, handleSelectDirectory, hasFiles }) {
  const { classes } = useStyles();
  return (
    <TableRow
      className={classes.tableRow}
      onClick={() => handleSelectDirectory(directory.path)}>
      <TableCell
        className={classes.tableCell}
        style={{ minWidth: 400, maxWidth: 700 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <img src={"/images/file_icons/folder_small.svg"} alt="folder_icon" />
          <Typography
            variant="body2"
            noWrap
            textOverflow={"ellipsis"}
            maxWidth={600}>
            {directory.name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell className={classes.tableCell}>
        {directory.updated_at ? formatDate(directory.updated_at) : "--"}
      </TableCell>
      {hasFiles && <TableCell className={classes.tableCell}></TableCell>}
      {hasFiles && <TableCell className={classes.tableCell}></TableCell>}
      {hasFiles && <TableCell className={classes.tableCell}></TableCell>}
      {hasFiles && (
        <TableCell className={classes.tableCell} align="right"></TableCell>
      )}
    </TableRow>
  );
}

function File({ file, handleSelectFile, handleDeleteFile, handleUpdateFile }) {
  const { classes } = useStyles();
  const {
    name,
    created_at,
    file_url,
    file_type,
    status,
    tags,
    directory,
    category,
  } = file;

  const handleOpenFileInNewTab = () => {
    if (notSupportedBrowserFiles.includes(file_type)) {
      window.open(file_url, "_blank", "noopener,noreferrer");
    } else {
      handleSelectFile(file, true);
    }
  };

  const tagsList = tags
    ? [tags.split(",")[0]].filter((tag) => tag && tag.trim() !== "")
    : [];

  return (
    <TableRow
      className={classes.tableRow}
      component={NavLink}
      to={`${VIEW_FILE_URL}/${file.id}`}
      style={{ textDecoration: "none" }}>
      <TableCell
        className={classes.tableCell}
        style={{ minWidth: 400, maxWidth: 700 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <img
            src={getFileIconImage(file_type)}
            alt="file_icon"
            className={classes.fileIcon}
          />
          <Box>
            <Tooltip title={name} arrow>
              <Typography
                variant="body2"
                noWrap
                textOverflow={"ellipsis"}
                maxWidth={600}>
                {name}
              </Typography>
            </Tooltip>
            <Tooltip title={directory} arrow>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                textOverflow={"ellipsis"}
                maxWidth={600}>
                {directory}
              </Typography>
            </Tooltip>
          </Box>
        </Box>
      </TableCell>
      <TableCell className={classes.tableCell}>
        <FileStatus status={status} />
      </TableCell>
      <TableCell className={classes.tableCell}>
        {formatDate(created_at)}
      </TableCell>
      <TableCell className={classes.tableCell}>
        {category && (
          <Chip
            size="small"
            key={category}
            label={category}
            sx={{ borderRadius: 1, fontWeight: 500 }}
          />
        )}
      </TableCell>
      <TableCell className={classes.tableCell}>
        {tagsList.length > 0 && (
          <Box display={"flex"} gap={1} ml={1.5} flexShrink={0}>
            {tagsList.slice(0, 2).map((tag) => (
              <Chip
                size="small"
                key={tag}
                label={tag}
                sx={{ borderRadius: 1, fontWeight: 500 }}
              />
            ))}
          </Box>
        )}
      </TableCell>
      <TableCell className={classes.tableCell} align="right">
        <Box
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}>
          <FileActionsMenu
            file={file}
            edge={null}
            handleDeleteFile={handleDeleteFile}
            handleUpdateFile={handleUpdateFile}
            handleOpenFileInNewTab={() => handleOpenFileInNewTab(file)}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
}

function OverflowingText({ text }) {
  const ref = useRef();
  const isOverflow = useIsOverflow(ref);
  return (
    <Tooltip title={text} arrow disableHoverListener={!isOverflow}>
      <Typography
        ref={ref}
        variant="body2"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: 650,
        }}>
        {text}
      </Typography>
    </Tooltip>
  );
}

export default ListView;
